import { useWorkflowStatus } from './WorkflowStatusProvider'
import { Item, Section } from '@react-stately/collections'
import { PropertyValuePill } from '../PropertyValuePill/PropertyValuePill'
import { generatePath, useHistory } from 'react-router-dom'
import { Key, ReactElement, useState } from 'react'
import { FOLDER_SETTINGS_PATH } from 'paths'
import { Tooltip } from '../Tooltip/Tooltip'
import { reportError } from '../../utils/error'
import { showErrorMessageToast } from '../../common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { InlineComboBox } from 'components/ComboBox/InlineComboBox'
import { DropdownTrigger } from 'components/DropdownTrigger/DropdownTrigger'
import Styles from './WorkflowStatusSelect.module.scss'
import { useInformalFeedbackRequests as useInformalFeedbackRequestsQuery } from '../../hooks/queries/informalFeedbackRequests/useInformalFeedbackRequests'
import { InformalFeedbackRequestState } from '../../api/clients/InformalFeedbackRequestsClient'
import { observer } from 'mobx-react-lite'
import { useRootStore } from '../../stores/RootStore'
import Layer from 'almanac-ui/icons/16/Layer'

const ReviewFrontIconContainer = observer(() => {
  const rootStore = useRootStore()
  const docStore = rootStore.docStore

  if (!docStore) {
    return null
  }

  return <ReviewFrontIcon />
})

const ReviewFrontIcon = observer(() => {
  const rootStore = useRootStore()
  const docStore = rootStore.docStore
  const { data } = useInformalFeedbackRequestsQuery({
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    articleKey: docStore!.articleId,
    states: [InformalFeedbackRequestState.Open],
    automated: true,
  })

  if (!data?.informal_feedback_requests || data.informal_feedback_requests.length < 1) {
    return null
  }

  if (docStore?.mainLayerId && data.informal_feedback_requests[0].layer.id !== docStore.mainLayerId) {
    return <Layer />
  }

  return null
})

interface WorkflowStatusSelectProps {
  size?: 'sm' | 'md'
}

export const WorkflowStatusSelect = ({ size = 'md' }: WorkflowStatusSelectProps) => {
  const { property, currentState, updateState, folder, canTransitionTo } = useWorkflowStatus()
  const history = useHistory()
  const [query, setQuery] = useState('')

  if (!currentState?.propertyValue || !folder || !property) {
    return null
  }

  const canEditFolder = ['owner', 'edit'].includes(folder.current_user_access)
  const handleUpdate = (key: Key | null) => {
    if (key === currentState?.propertyValue.id + '') return

    if (key === 'folder-settings') {
      return history.push(generatePath(FOLDER_SETTINGS_PATH, { folderSlug: folder.handbook.seo_string }))
    }
    updateState(key as string).catch((e) => {
      if (e instanceof Error) {
        reportError(e, { severity: 'error' })
        showErrorMessageToast('There was a problem updating the status of the file. Our team has been notified.')
      }
    })
  }

  const disabledKeys = canEditFolder ? [] : ['folder-settings']
  const items: Array<ReactElement> = []
  const FrontIcon = currentState.exitAction?.type === 'reviews.close' ? ReviewFrontIconContainer : undefined

  property.property_values.forEach((propertyValue) => {
    const canTransition = canTransitionTo(propertyValue.id + '')
    if (!canTransition) {
      disabledKeys.push(propertyValue.id + '')
    }

    items.push(
      <Item key={propertyValue.id} textValue={propertyValue.value}>
        <Tooltip label="You do not have permission change to this status" hide={canTransition}>
          <span>
            <PropertyValuePill frontIcon={FrontIcon} propertyValue={propertyValue} size={size} />
          </span>
        </Tooltip>
      </Item>,
    )
  })

  if (disabledKeys.length >= property.property_values.length) {
    return <PropertyValuePill frontIcon={FrontIcon} propertyValue={currentState.propertyValue} size={size} />
  }

  return (
    <DropdownTrigger popoverClassName={Styles.popover}>
      <PropertyValuePill frontIcon={FrontIcon} propertyValue={currentState.propertyValue} withCaret size={size} />
      {(close) => (
        <InlineComboBox
          label="Change status"
          onSelectionChange={(key) => {
            handleUpdate(key)
            setQuery('')
            close()
          }}
          selectedKey={currentState.propertyValue.id + ''}
          placeholder="Change status"
          disabledKeys={disabledKeys}
          onInputChange={setQuery}
          inputValue={query}
        >
          <Section>{items}</Section>
          <Section>
            <Item key="folder-settings" textValue="Edit Statuses">
              <Tooltip label="You do not have permission to edit the folder settings" hide={canEditFolder}>
                <span>Edit Statuses</span>
              </Tooltip>
            </Item>
          </Section>
        </InlineComboBox>
      )}
    </DropdownTrigger>
  )
}
